/**
 * HitlFileUploadDecisionPanel — two-dropzone HITL response form for the
 * **Carrier Portal Complete** gate (and any future gates with the same
 * structural needs).
 *
 * The wire shape carried in / out is:
 *
 *   in  (question): { type: "file_upload_decision", question, message,
 *                     policy_label?, other_label?, instance_id, ... }
 *
 *   out (response): { decision: "approve" | "reject",
 *                     policy_documents:  [{name, mime_type, bytes_b64}],
 *                     other_documents:   [{name, mime_type, bytes_b64}],
 *                     reject_reason?:    "<required when decision=reject>" }
 *
 * **Flat top-level keys, not nested under field_values.** The substrate's
 * resume_workflow tool (workflows/src/workflows/tools/resume_cancel.py:267)
 * promotes the top-level ``decision`` to ``response.action`` and sweeps the
 * remaining top-level keys into ``response.field_values``. Workflows then
 * read ``hitl_answer.field_values.policy_documents`` (single-nested) — the
 * carrier-mortgagee-update workflow's ``_carrier_doc_buckets_from_state``
 * depends on this exact path.
 *
 * The panel previously emitted ``{instance_id, field_values: {decision, ...}}``
 * (nested). The substrate had no top-level ``decision`` to extract and
 * stored the whole body under ``response.field_values``, double-nesting the
 * uploaded files at ``hitl_answer.field_values.field_values.policy_documents``.
 * Workflows looking at the single-nested path saw nothing and proceeded
 * without attaching the documents. Instance
 * ``e802ff3c-caae-4cd1-becb-9c7c0b062cc2`` (EMUW, 2026-05-29) hit this
 * exact bug: the AM uploaded two files, the panel emitted the nested shape,
 * the substrate stored them double-nested, and ``attach_carrier_documents``
 * found nothing to attach.
 *
 * Two named buckets — the workflow routes the "policy" bucket into Epic's
 * "Policy Documents" folder and leaves the "other" bucket at the activity
 * top level. Reject discards uploads in both buckets and aborts the
 * instance with `reject_reason` (mandatory).
 *
 * Shared between ora and the Outlook plugin — transport (Remix fetcher /
 * orchestratorApi) stays app-specific via the parent's onSubmit. The
 * /resume API URL carries the instance_id, so the panel no longer ships
 * it in the body.
 */

import CloseIcon from "@mui/icons-material/Close";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import { IconButton, Modal as MuiModal } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { FileUploadDropzone } from "~/common/components/FileUploadDropzone";
import { renderInlineBold } from "../renderInlineBold";

export interface HitlFileUploadDecisionPanelProps {
  /**
   * Substrate instance id. Kept for API parity with sibling panels but no
   * longer used in the submit payload — the /resume API URL carries the
   * instance_id, so the body is the flat decision + buckets shape
   * documented in the file-level docstring.
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  instanceId: string;
  /** The HITL question payload, as forwarded from the substrate. */
  hitlQuestion: Record<string, unknown> | null | undefined;
  /**
   * Parent's submitter — receives the flat
   * ``{decision, policy_documents, other_documents, reject_reason?}`` payload.
   * See the file-level docstring for the wire-shape contract.
   */
  onSubmit: (values: Record<string, unknown>) => void | Promise<void>;
  /** Externally controlled submitting state for parents that prefer to manage it. */
  submittingOverride?: boolean;
}

interface EncodedFile {
  name: string;
  mime_type: string;
  bytes_b64: string;
}

const ACCEPTED_FILE_TYPES = [".pdf", ".png", ".jpg", ".jpeg", ".docx", ".doc"];
const MAX_FILE_BYTES = 25 * 1024 * 1024;
const MAX_FILES_PER_BUCKET = 10;

export function HitlFileUploadDecisionPanel({
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  instanceId: _instanceId,
  hitlQuestion,
  onSubmit,
  submittingOverride,
}: HitlFileUploadDecisionPanelProps) {
  const [policyFiles, setPolicyFiles] = useState<File[]>([]);
  const [otherFiles, setOtherFiles] = useState<File[]>([]);
  const [policyError, setPolicyError] = useState<string>("");
  const [otherError, setOtherError] = useState<string>("");
  const [rejecting, setRejecting] = useState(false);
  const [rejectReason, setRejectReason] = useState<string>("");
  const [internalSubmitting, setInternalSubmitting] = useState(false);
  const submitting = submittingOverride ?? internalSubmitting;

  const heading = getString(hitlQuestion, "question") ?? "Carrier Portal Complete";
  // ``pending_action`` is the substrate's canonical "what the AM must do now"
  // field — the one field every HITL surface is contracted to render. This
  // panel historically rendered only ``question``/``message`` and dropped it,
  // so gates that put their instructions there (e.g. the manual carrier gate:
  // "open the carrier's portal, sign in, update the mortgagee…") showed the AM
  // no instructions at all (substrate instance 0ff8c712-9cd4-4adb-9d91-0cb160730a00).
  const pendingAction = getString(hitlQuestion, "pending_action");
  const rawMessage = getString(hitlQuestion, "message");
  // De-dupe: some gates set ``message`` identical to ``pending_action`` so an
  // older add-in (which renders only ``message``) still shows the instructions.
  // Render ``message`` only when it adds something beyond ``pending_action``.
  const message =
    rawMessage && rawMessage !== pendingAction ? rawMessage : undefined;
  // A file_upload_decision gate must NEVER render instruction-less — the AM
  // reported a blank gate (heading + dropzones only) on instance e83d7005
  // even though the persisted question carried both fields, so whatever
  // payload reached this panel had neither. Show a generic fallback so the
  // AM is never stranded, and log the keys that DID arrive so the next
  // occurrence is diagnosable from the console.
  const missingInstructions = !pendingAction && !message;
  if (missingInstructions && typeof console !== "undefined") {
    // eslint-disable-next-line no-console
    console.warn(
      "HitlFileUploadDecisionPanel: question payload has no pending_action/message — received keys:",
      hitlQuestion ? Object.keys(hitlQuestion) : hitlQuestion,
    );
  }
  const policyLabel =
    getString(hitlQuestion, "policy_label") ?? "Policy Documents";
  // ``other_label === null`` (explicit) suppresses the second bucket — used
  // by the decpage gate, which only needs one bucket plus a saved-file
  // preview block above it.
  const otherLabelRaw = hitlQuestion?.other_label;
  const singleBucket =
    hitlQuestion?.single_bucket === true || otherLabelRaw === null;
  const otherLabel =
    typeof otherLabelRaw === "string" && otherLabelRaw
      ? otherLabelRaw
      : "Other Documents";

  // Saved-file preview block (decpage gate). Shape:
  //   { source: "manual"|"auto_poll", name, mime_type, size_bytes,
  //     preview_data_uri?, volume_path? } | null
  const savedDecPage =
    hitlQuestion && typeof hitlQuestion === "object"
      ? (hitlQuestion as Record<string, unknown>).saved_dec_page
      : null;

  const totalFiles =
    policyFiles.length + (singleBucket ? 0 : otherFiles.length);
  const canApprove =
    !submitting &&
    !policyError &&
    !otherError &&
    (totalFiles > 0 ||
      (singleBucket && isSavedDecPage(savedDecPage)));
  const rejectReasonMissing = rejectReason.trim().length === 0;

  const makeFileHandler =
    (
      bucket: File[],
      setBucket: (next: File[]) => void,
      setBucketError: (next: string) => void,
    ) =>
    (incoming: FileList) => {
      setBucketError("");
      const accepted: File[] = [];
      const rejectedNames: string[] = [];
      Array.from(incoming).forEach((f) => {
        if (f.size > MAX_FILE_BYTES) {
          rejectedNames.push(f.name);
          return;
        }
        accepted.push(f);
      });
      const combined = [...bucket, ...accepted];
      if (combined.length > MAX_FILES_PER_BUCKET) {
        setBucketError(`Up to ${MAX_FILES_PER_BUCKET} files per bucket.`);
        return;
      }
      if (rejectedNames.length > 0) {
        const list = rejectedNames.map((n) => `"${n}"`).join(", ");
        setBucketError(
          `${list} exceeded the 25 MB per-file limit and ${
            rejectedNames.length === 1 ? "was" : "were"
          } skipped.`,
        );
      }
      setBucket(combined);
    };

  const handlePolicyChange = makeFileHandler(
    policyFiles,
    setPolicyFiles,
    setPolicyError,
  );
  const handleOtherChange = makeFileHandler(
    otherFiles,
    setOtherFiles,
    setOtherError,
  );

  const handlePolicyRemove = (index: number) => {
    setPolicyError("");
    setPolicyFiles(policyFiles.filter((_, i) => i !== index));
  };
  const handleOtherRemove = (index: number) => {
    setOtherError("");
    setOtherFiles(otherFiles.filter((_, i) => i !== index));
  };

  const submitApprove = async () => {
    if (!canApprove) return;
    setInternalSubmitting(true);
    try {
      const [policy_documents, other_documents] = await Promise.all([
        Promise.all(policyFiles.map(encodeFile)),
        Promise.all(otherFiles.map(encodeFile)),
      ]);
      // Flat keys at top level — see the file-level docstring for why.
      // The substrate's resume_workflow extracts `decision` as the action
      // and sweeps the rest into field_values; nested {field_values: {...}}
      // would double-nest the uploaded buckets (instance e802ff3c-… bug).
      await Promise.resolve(
        onSubmit({
          decision: "approve",
          policy_documents,
          other_documents,
        }),
      );
    } catch (err) {
      const msg = err instanceof Error ? err.message : "File upload failed.";
      setPolicyError(msg);
    } finally {
      setInternalSubmitting(false);
    }
  };

  const submitReject = async () => {
    if (rejectReasonMissing || submitting) return;
    setInternalSubmitting(true);
    try {
      // Uploaded files are discarded on reject — per the design, the abort
      // path never reaches Epic or Outlook, so encoding + shipping bytes
      // is wasted work. Same flat-keys contract as approve.
      await Promise.resolve(
        onSubmit({
          decision: "reject",
          reject_reason: rejectReason.trim(),
          policy_documents: [],
          other_documents: [],
        }),
      );
    } finally {
      setInternalSubmitting(false);
    }
  };

  return (
    <div
      data-testid="hitl-file-upload-decision-panel"
      className="flex flex-col gap-4 rounded-md border border-primary-gray-light-200 bg-base-white px-4 py-3"
    >
      <h3
        className="text-base font-semibold text-primary-gray-light-800"
        data-testid="hitl-file-upload-decision-heading"
      >
        {heading}
      </h3>
      {pendingAction && (
        <p
          className="text-sm text-primary-gray-light-700 whitespace-pre-wrap"
          data-testid="hitl-file-upload-decision-pending-action"
        >
          {renderInlineBold(pendingAction)}
        </p>
      )}
      {message && (
        <p
          className="text-sm text-primary-gray-light-700 whitespace-pre-wrap"
          data-testid="hitl-file-upload-decision-message"
        >
          {renderInlineBold(message)}
        </p>
      )}
      {missingInstructions && (
        <p
          className="text-sm text-primary-gray-light-700 whitespace-pre-wrap"
          data-testid="hitl-file-upload-decision-fallback-instructions"
        >
          This step needs your input: complete the required change, then
          upload the supporting document(s) below and confirm to continue.
        </p>
      )}

      <SavedDecPageBlock saved={savedDecPage} />

      <section
        data-testid="hitl-file-upload-decision-policy-bucket"
        className="flex flex-col gap-2"
      >
        <div className="flex items-baseline justify-between">
          <h4 className="text-sm font-semibold text-primary-gray-light-800">
            {policyLabel}
          </h4>
          {!singleBucket && (
            <span className="text-[11px] text-primary-gray-light-500">
              → Epic &ldquo;Policy Documents&rdquo; folder
            </span>
          )}
        </div>
        <FileUploadDropzone
          compact
          onFileChange={handlePolicyChange}
          uploadedFiles={policyFiles as unknown as FileList}
          onFileRemove={handlePolicyRemove}
          onClearAll={() => {
            setPolicyError("");
            setPolicyFiles([]);
          }}
          acceptedFileTypes={ACCEPTED_FILE_TYPES}
          maxFiles={MAX_FILES_PER_BUCKET}
          fileUploadMaxSize={MAX_FILE_BYTES}
          error={policyError}
        />
      </section>

      {!singleBucket && (
        <section
          data-testid="hitl-file-upload-decision-other-bucket"
          className="flex flex-col gap-2"
        >
          <div className="flex items-baseline justify-between">
            <h4 className="text-sm font-semibold text-primary-gray-light-800">
              {otherLabel}
            </h4>
            <span className="text-[11px] text-primary-gray-light-500">
              → Activity top level (no folder)
            </span>
          </div>
          <FileUploadDropzone
            compact
            onFileChange={handleOtherChange}
            uploadedFiles={otherFiles as unknown as FileList}
            onFileRemove={handleOtherRemove}
            onClearAll={() => {
              setOtherError("");
              setOtherFiles([]);
            }}
            acceptedFileTypes={ACCEPTED_FILE_TYPES}
            maxFiles={MAX_FILES_PER_BUCKET}
            fileUploadMaxSize={MAX_FILE_BYTES}
            error={otherError}
          />
        </section>
      )}

      <div className="flex flex-col gap-2 pt-2 border-t border-primary-gray-light-200">
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => setRejecting((v) => !v)}
            disabled={submitting}
            data-testid="hitl-file-upload-decision-reject-toggle"
            className="px-3 py-2 text-sm font-bold rounded-lg btn-error-secondary"
          >
            Reject
          </button>
          <button
            type="button"
            disabled={!canApprove}
            onClick={submitApprove}
            data-testid="hitl-file-upload-decision-approve"
            className={
              canApprove
                ? "px-3 py-2 text-sm font-bold rounded-lg bg-sunrise border border-sunrise text-primary-gray-light-800 hover:shadow-[0px_0px_2px_2px_rgba(255,164,0,0.24)]"
                : "px-3 py-2 text-sm font-bold rounded-lg bg-primary-gray-light-200 border border-primary-gray-light-300 text-primary-gray-light-500 cursor-not-allowed"
            }
          >
            {submitting && !rejecting ? "Submitting…" : "Yes — Proceed"}
          </button>
        </div>
        {!canApprove && totalFiles === 0 && !isSavedDecPage(savedDecPage) && (
          <p
            className="text-[11px] text-primary-gray-light-500 text-right"
            data-testid="hitl-file-upload-decision-empty-hint"
          >
            {singleBucket
              ? "Attach a document to proceed."
              : `Attach at least one document — ${policyLabel} or ${otherLabel}, one or both — to proceed.`}
          </p>
        )}

        {rejecting && (
          <div className="border border-primary-error-300 bg-primary-error-50 rounded-md px-3 py-2 flex flex-col gap-3">
            <p className="text-xs text-primary-error-700">
              Rejection aborts the workflow — uploaded files are discarded
              and the instance is closed.
            </p>
            <div className="flex flex-col gap-1">
              <label
                className="text-[11px] uppercase tracking-wide text-primary-error-700 font-semibold"
                htmlFor="hitl-file-upload-decision-reject-reason"
              >
                Reason <span className="text-primary-error-400">*</span>{" "}
                <span className="normal-case text-primary-error-500">
                  (required — what was wrong?)
                </span>
              </label>
              <textarea
                id="hitl-file-upload-decision-reject-reason"
                rows={2}
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                disabled={submitting}
                required
                aria-required="true"
                aria-invalid={rejectReasonMissing}
                placeholder="A reason is required to reject…"
                autoFocus
                data-testid="hitl-file-upload-decision-reject-reason"
                className="rounded-md border border-primary-error-300 bg-base-white px-3 py-2 text-sm focus:outline-none focus:border-primary-error-500 focus:ring-1 focus:ring-primary-error-500 disabled:opacity-50 resize-none"
              />
              {rejectReasonMissing && (
                <p
                  className="text-[11px] text-primary-error-600"
                  data-testid="hitl-file-upload-decision-reject-reason-error"
                >
                  Please enter a reason before rejecting.
                </p>
              )}
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={submitting || rejectReasonMissing}
                onClick={submitReject}
                data-testid="hitl-file-upload-decision-reject-confirm"
                className="btn-error-primary text-xs font-semibold px-3 py-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? "Aborting…" : "Yes, Reject"}
              </button>
              <button
                type="button"
                disabled={submitting}
                onClick={() => {
                  setRejecting(false);
                  setRejectReason("");
                }}
                className="btn-secondary-gray text-xs font-semibold !px-3 !py-1.5"
                data-testid="hitl-file-upload-decision-reject-cancel"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function getString(
  obj: Record<string, unknown> | null | undefined,
  key: string,
): string | undefined {
  if (!obj) return undefined;
  const v = obj[key];
  return typeof v === "string" ? v : undefined;
}

interface SavedDecPage {
  // Substrate-side cascade in
  // ``carrier_mortgagee_update._build_saved_dec_page_block``:
  //   "manual"                  → state.decpage_uploaded (previous DECPAGE_WAIT upload)
  //   "auto_poll"               → state.decpage_volume_path (L2 / auto-poll)
  //   "carrier_portal_complete" → PDF already uploaded at the earlier
  //                               carrier_portal_complete bucket — surfaced
  //                               here so the AM doesn't have to upload twice
  //                               (instance 885ff8e0-3988-4b51-b9ff-991567c14254
  //                               on 2026-05-29: the AM uploaded "Dec Page.pdf"
  //                               at carrier_portal_complete; without this
  //                               source the panel rendered MISSING and asked
  //                               for the same file again).
  source: "manual" | "auto_poll" | "carrier_portal_complete";
  name: string;
  mime_type: string | null;
  size_bytes: number | null;
  preview_data_uri?: string;
  volume_path?: string;
}

function isSavedDecPage(v: unknown): v is SavedDecPage {
  if (typeof v !== "object" || v === null) return false;
  const rec = v as Record<string, unknown>;
  if (typeof rec.name !== "string") return false;
  return (
    rec.source === "manual" ||
    rec.source === "auto_poll" ||
    rec.source === "carrier_portal_complete"
  );
}

function formatBytes(b: number | null): string {
  if (b == null || b <= 0) return "";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(2)} MB`;
}

/**
 * Convert a base64 ``data:`` URI into a blob: URL.
 *
 * Modern Chrome (and other Chromium browsers since 2024) refuse to render
 * PDFs supplied via ``data:`` URIs in either ``<object>`` or ``<iframe>`` —
 * the element paints its border but the embedded PDF viewer never loads, so
 * the user sees an empty outlined box. ``URL.createObjectURL`` against a Blob
 * sidesteps the restriction, which is why we hand the iframe a blob URL.
 */
function useBlobUrl(dataUri: string | undefined): string | undefined {
  const [blobUrl, setBlobUrl] = useState<string | undefined>(undefined);
  useEffect(() => {
    if (!dataUri) {
      setBlobUrl(undefined);
      return;
    }
    if (typeof URL?.createObjectURL !== "function") return;
    let cancelled = false;
    let createdUrl: string | undefined;
    (async () => {
      try {
        const response = await fetch(dataUri);
        const blob = await response.blob();
        if (cancelled) return;
        createdUrl = URL.createObjectURL(blob);
        setBlobUrl(createdUrl);
      } catch {
        if (!cancelled) setBlobUrl(undefined);
      }
    })();
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [dataUri]);
  return blobUrl;
}

function DecPageLightbox({
  open,
  onClose,
  name,
  src,
  kind,
}: {
  open: boolean;
  onClose: () => void;
  name: string;
  src: string | undefined;
  kind: "pdf" | "image";
}) {
  return (
    <MuiModal
      open={open}
      onClose={onClose}
      aria-label={`Enlarged preview of ${name}`}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        className="relative bg-base-white rounded-lg shadow-2xl flex flex-col w-[90vw] h-[90vh] p-3"
        role="document"
        data-testid="hitl-saved-dec-page-lightbox"
      >
        <div className="flex items-center justify-between gap-3 px-1 pb-2 border-b border-primary-gray-light-100">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-base">📄</span>
            <span className="text-sm font-semibold text-primary-gray-light-900 truncate">
              {name}
            </span>
          </div>
          <IconButton
            onClick={onClose}
            aria-label="Close preview"
            size="small"
            data-testid="hitl-saved-dec-page-lightbox-close"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>
        <div className="flex-1 min-h-0 mt-2 rounded border border-primary-gray-light-200 bg-primary-gray-light-50 overflow-hidden">
          {src && kind === "pdf" && (
            <iframe
              src={src}
              title={`Preview of ${name}`}
              className="w-full h-full"
            />
          )}
          {src && kind === "image" && (
            <div className="w-full h-full flex items-center justify-center p-3">
              <img
                src={src}
                alt={`Preview of ${name}`}
                className="max-w-full max-h-full object-contain"
              />
            </div>
          )}
          {!src && (
            <div className="w-full h-full flex items-center justify-center text-sm text-primary-gray-light-600">
              Loading preview…
            </div>
          )}
        </div>
      </div>
    </MuiModal>
  );
}

function SavedDecPageBlock({ saved }: { saved: unknown }) {
  const validSaved = isSavedDecPage(saved) ? saved : null;
  const previewDataUri = validSaved?.preview_data_uri;
  const blobUrl = useBlobUrl(previewDataUri);
  // Images render fine from data URIs; only PDFs need the blob workaround.
  const isImage = (validSaved?.mime_type ?? "").startsWith("image/");
  const isPdf = (validSaved?.mime_type ?? "") === "application/pdf";
  const imageSrc = previewDataUri;
  const pdfSrc = blobUrl ?? previewDataUri;
  const lightboxSrc = isPdf ? pdfSrc : imageSrc;
  const canEnlarge = !!previewDataUri && (isPdf || isImage);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const enlarge = useMemo(
    () => () => setLightboxOpen(true),
    [],
  );

  // null/undefined/wrong-shape → MISSING badge so the AM has a clear signal.
  if (!validSaved) {
    return (
      <div
        data-testid="hitl-saved-dec-page-missing"
        className="flex items-center gap-2 rounded-md border border-primary-warning-300 bg-primary-warning-50 px-3 py-2"
      >
        <span className="text-xs font-bold uppercase tracking-wide text-primary-warning-700">
          ⚠ MISSING
        </span>
        <span className="text-xs text-primary-warning-700">
          No Declarations page saved yet. Drop one below to proceed.
        </span>
      </div>
    );
  }

  const sizeLabel = formatBytes(validSaved.size_bytes);
  const sourceLabel = (() => {
    switch (validSaved.source) {
      case "auto_poll":
        return "L2 Volume auto-poll";
      case "carrier_portal_complete":
        return "From carrier portal upload";
      default:
        return "Manual upload";
    }
  })();

  return (
    <div
      data-testid="hitl-saved-dec-page"
      className="flex flex-col gap-2 rounded-md border border-primary-gray-light-200 bg-base-white px-3 py-2"
    >
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-xs font-bold uppercase tracking-wide text-primary-gray-light-800">
          Currently saved
        </span>
        <span className="text-[11px] text-primary-gray-light-500">
          {sourceLabel}
          {sizeLabel ? ` · ${sizeLabel}` : ""}
        </span>
      </div>
      <div className="flex items-center justify-between gap-2">
        <div className="text-sm text-primary-gray-light-800 min-w-0">
          <span className="mr-1">📄</span>
          <span data-testid="hitl-saved-dec-page-name">{validSaved.name}</span>
        </div>
        {canEnlarge && (
          <button
            type="button"
            onClick={enlarge}
            aria-label={`Enlarge preview of ${validSaved.name}`}
            data-testid="hitl-saved-dec-page-enlarge"
            className="inline-flex items-center gap-1 text-[11px] font-semibold text-primary-gray-light-700 hover:text-primary-gray-light-900 hover:underline shrink-0"
          >
            <OpenInFullIcon style={{ fontSize: 12 }} />
            Enlarge
          </button>
        )}
      </div>
      {previewDataUri && isPdf && (
        // Iframe is NOT wrapped in a click target so the browser's native PDF
        // viewer keeps its scroll + zoom controls. The "Enlarge" affordance in
        // the header row above is the entry point to the lightbox.
        <iframe
          src={pdfSrc}
          title={`Preview of ${validSaved.name}`}
          aria-label={`Preview of ${validSaved.name}`}
          className="w-full h-80 rounded border border-primary-gray-light-200"
          data-testid="hitl-saved-dec-page-preview-pdf"
          data-source-uri={previewDataUri}
        />
      )}
      {previewDataUri && isImage && (
        <button
          type="button"
          onClick={enlarge}
          aria-label={`Open ${validSaved.name} in a larger viewer`}
          className="block w-full p-0 border-0 bg-transparent cursor-zoom-in focus:outline-none focus:ring-2 focus:ring-primary-gray-light-300 rounded"
        >
          <img
            src={imageSrc}
            alt={`Preview of ${validSaved.name}`}
            className="max-w-full max-h-80 object-contain rounded border border-primary-gray-light-200 mx-auto"
            data-testid="hitl-saved-dec-page-preview-image"
          />
        </button>
      )}
      {!previewDataUri && validSaved.volume_path && (
        <p className="text-[11px] text-primary-gray-light-500" data-testid="hitl-saved-dec-page-volume-path">
          From: <code>{validSaved.volume_path}</code>
        </p>
      )}
      {canEnlarge && (
        <DecPageLightbox
          open={lightboxOpen}
          onClose={() => setLightboxOpen(false)}
          name={validSaved.name}
          src={lightboxSrc}
          kind={isPdf ? "pdf" : "image"}
        />
      )}
    </div>
  );
}

async function encodeFile(file: File): Promise<EncodedFile> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const comma = result.indexOf(",");
      resolve({
        name: file.name,
        mime_type: file.type || "application/octet-stream",
        bytes_b64: comma >= 0 ? result.slice(comma + 1) : result,
      });
    };
    reader.onerror = () =>
      reject(reader.error ?? new Error("FileReader failed"));
    reader.readAsDataURL(file);
  });
}

export default HitlFileUploadDecisionPanel;
